import { Link } from 'react-router-dom';
import { ArrowRightIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { HomeLazySection } from '../HomeLazySection';
import { HomeStaticImage } from '../HomeStaticImage';
import { homeCta } from '../homeCtaStyles';
import { HOME_AUDIENCE_EDITS, HOME_SHOP_CATEGORIES } from '../homeContent';

type StartShoppingCardProps = Readonly<{
  title: string;
  description: string;
  cta: string;
  link: string;
  image: string;
  tone: string;
  accent: string;
  metaLine?: string;
  titleSize?: 'edit' | 'category';
}>;

function StartShoppingCard({
  title,
  description,
  cta,
  link,
  image,
  tone,
  accent,
  metaLine,
  titleSize = 'edit',
}: StartShoppingCardProps) {
  const titleClass =
    titleSize === 'edit'
      ? 'text-2xl md:text-3xl'
      : 'text-xl md:text-2xl';

  return (
    <Link
      to={link}
      className="group relative block min-h-[212px] overflow-hidden rounded-[28px] border border-purple-100/70 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl md:min-h-[224px]"
      style={{ background: tone }}
    >
      <div
        className="pointer-events-none absolute -top-24 -right-24 h-64 w-64 rounded-full opacity-30 blur-3xl"
        style={{ backgroundColor: accent }}
      />

      <div className="absolute inset-0">
        <HomeStaticImage
          src={image}
          alt={title}
          className="absolute right-0 top-0 h-full w-[42%] object-cover object-top opacity-90 transition-opacity group-hover:opacity-100"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(90deg, rgba(255,255,255,0.82) 0%, rgba(255,255,255,0.65) 35%, rgba(255,255,255,0.2) 62%, rgba(255,255,255,0) 100%)',
          }}
        />
      </div>

      <div className="relative z-10 flex flex-col gap-2.5 p-5 pr-[34%] sm:pr-[38%] md:gap-3 md:p-6">
        <p
          className={`${titleClass} w-max max-w-[calc(100%+0.5rem)] whitespace-nowrap rounded-2xl border border-white/80 bg-white/90 px-3 py-2 font-extrabold tracking-tight text-slate-900 shadow-sm`}
        >
          {title}
        </p>

        <p className="w-max max-w-[calc(100%+0.5rem)] whitespace-nowrap text-sm leading-snug text-slate-700">
          {description}
        </p>

        {metaLine ? (
          <p className="w-max max-w-[calc(100%+0.5rem)] whitespace-nowrap text-xs text-slate-500">
            {metaLine}
          </p>
        ) : null}

        <div className="mt-1 inline-flex w-fit items-center justify-center rounded-full border border-slate-100 bg-white px-5 py-2.5 text-sm font-semibold text-slate-900 shadow-sm transition-colors group-hover:border-slate-900 group-hover:bg-slate-900 group-hover:text-white">
          {cta}
          <ArrowRightIcon className="ml-2 h-4 w-4" />
        </div>
      </div>
    </Link>
  );
}

/** Merged discovery — shop by edit + shop by product (original editorial cards). */
export function HomeStartShoppingSection() {
  return (
    <HomeLazySection
      className="relative py-12 md:py-16 bg-gradient-to-b from-white via-white to-[#fbfaff]"
      placeholderMinHeight="480px"
    >
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-7 flex flex-col gap-3 md:mb-9 md:flex-row md:items-end md:justify-between">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-3 py-1 text-xs font-semibold text-white">
              <SparklesIcon className="h-4 w-4" />
              Start shopping
            </div>
            <h2 className="mt-4 text-2xl font-extrabold tracking-tight text-slate-900 md:text-4xl">
              Find your lane. Pick your piece.
            </h2>
            <p className="mt-2 text-slate-600">
              Shop by style or by product—one path into the catalog.
            </p>
          </div>
          <Link to="/shop" className={`${homeCta.primary} w-full md:w-auto`}>
            Shop all
            <ArrowRightIcon className="ml-2 h-4 w-4" />
          </Link>
        </div>

        <div className="space-y-10">
          <div>
            <p className="mb-4 text-xs font-semibold uppercase tracking-wide text-slate-400">
              Shop by edit
            </p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 md:gap-5">
              {HOME_AUDIENCE_EDITS.map((edit) => (
                <StartShoppingCard
                  key={edit.title}
                  title={edit.title}
                  description={edit.description}
                  cta={edit.cta}
                  link={edit.link}
                  image={edit.image}
                  tone={edit.tone}
                  accent={edit.accent}
                  titleSize="edit"
                />
              ))}
            </div>
          </div>

          <div>
            <p className="mb-4 text-xs font-semibold uppercase tracking-wide text-slate-400">
              Shop by product
            </p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 md:gap-5">
              {HOME_SHOP_CATEGORIES.map((category) => (
                <StartShoppingCard
                  key={category.title}
                  title={category.title}
                  description={category.description}
                  cta={category.cta}
                  link={category.link}
                  image={category.image}
                  tone={category.tone}
                  accent={category.accent}
                  metaLine={category.designsAvailableLine}
                  titleSize="category"
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </HomeLazySection>
  );
}
